/**
 * Typed calls against services/office.
 *
 * Paths are relative to `/api/office`, which the dev proxy (and nginx in prod)
 * rewrites to the service's `/api/v1.0/...`.
 */
import { officeApi } from "./client";
import type {
  Announcement,
  AnalyticsDashboard,
  AnalyticsEnvelope,
  AnalyticsQuery,
  AttendanceRow,
  CompositionRow,
  DailyReport,
  FloorDesks,
  FloorSummary,
  InsightsEnvelope,
  Knock,
  LeaveBalance,
  LeaveCalendarEntry,
  LeaveCoverage,
  LeavePolicy,
  LeaveRequest,
  LeaveType,
  LiveBoard,
  ManualStatus,
  MapLayout,
  MeResponse,
  OccupancyData,
  OfficeNotification,
  OfficeTask,
  OrganizationSummary,
  OrgMetrics,
  OrgSettings,
  Paginated,
  PresenceUser,
  RollupGroup,
  RollupPeriod,
  TasksLoad,
  TimerSnapshot,
  UserDetail,
  UserSummary,
  UtilizationRow,
  Workday,
} from "./types";

function query(params: Record<string, string | number | boolean | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== "") search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

export const officeService = {
  // ---------------------------------------------------------------- me
  getMe: () => officeApi.get<MeResponse>("/me/"),
  updatePreferences: (body: Partial<Pick<UserDetail, "job_title" | "timezone" | "share_calendar_titles">>) =>
    officeApi.patch<UserDetail>("/me/", body),
  setStatus: (status: ManualStatus, statusMessage?: string) =>
    officeApi.patch<PresenceUser>("/me/status/", {
      status,
      status_message: statusMessage ?? "",
    }),
  getTimer: () => officeApi.get<TimerSnapshot>("/me/timer/"),
  getMyBalances: (year?: number) =>
    officeApi.get<LeaveBalance[]>(`/me/balances/${query({ year })}`),

  // ------------------------------------------------------- the workday
  getWorkday: () => officeApi.get<Workday>("/me/workday/"),
  checkIn: (body: { finish_onboarding?: boolean } = {}) =>
    officeApi.post<Workday>("/me/workday/check-in/", body),
  checkOut: () => officeApi.post<Workday>("/me/workday/check-out/", {}),
  getMyFloorDesks: () => officeApi.get<FloorDesks>("/me/desks/"),
  joinDeskWaitlist: () => officeApi.post<{ waitlisted: boolean }>("/me/desks/notify/", {}),

  // ------------------------------------------------------------ the floor
  getMap: (mapId?: string) =>
    officeApi.get<MapLayout>(`/office/map/${query({ map_id: mapId })}`),
  listFloors: (organizationId?: string) =>
    officeApi.get<{
      organization_id: string;
      is_other_organization: boolean;
      current_floor_id: string | null;
      floors: FloorSummary[];
    }>(`/office/floors/${query({ organization_id: organizationId })}`),
  createFloor: (body: {
    name: string;
    width?: number;
    height?: number;
    is_default?: boolean;
    with_default_layout?: boolean;
    organization_id?: string;
  }) => officeApi.post<MapLayout>("/office/floors/", body),
  listOrganizations: () =>
    officeApi.get<{ organizations: OrganizationSummary[] }>("/admin/organizations/"),
  updateFloor: (id: string, body: Partial<FloorSummary>) =>
    officeApi.patch<MapLayout>(`/office/floors/${id}/`, body),
  deleteFloor: (id: string) => officeApi.delete(`/office/floors/${id}/`),
  switchFloor: (mapId: string) =>
    officeApi.post<{ floor: MapLayout; presence: PresenceUser }>("/me/floor/", {
      map_id: mapId,
    }),
  updateMap: (body: unknown) => officeApi.patch<MapLayout>("/office/map/", body),
  getZoneCall: (zoneId: string) =>
    officeApi.get<{
      zone_id: string;
      zone_name: string;
      room: string;
      occupants: { user_id: string; name: string; avatar_url: string }[];
    }>(`/office/zones/${zoneId}/call/`),
  /**
   * The meeting an accepted knock opens — the same shape as a room's call.
   *
   * `/knocks/`, not `/office/knocks/`: the knock routes are registered on the
   * router, which mounts at the API root. Only the hand-written `path()`
   * entries — zones, map — carry an `office/` prefix. Getting that wrong 404s,
   * and the panel then reports it as "this room has no call".
   */
  getKnockCall: (knockId: string) =>
    officeApi.get<{
      knock_id: string;
      title: string;
      room: string;
      participants: { user_id: string; name: string; avatar_url: string }[];
    }>(`/knocks/${knockId}/call/`),
  getPresence: (
      opts: { includeOffline?: boolean; team?: string; scope?: "org" } = {},
    ) =>
    officeApi.get<{ users: PresenceUser[] }>(
      `/office/presence/${query({
        include_offline: opts.includeOffline,
        team: opts.team,
        // "org" returns everybody wherever they are standing, which is what a
        // search across floors needs; the default is this floor only.
        scope: opts.scope,
      })}`,
    ),

  // ----------------------------------------------------------------- desks
  listDesks: () => officeApi.get<Paginated<DeskRow>>("/desks/"),
  claimDesk: (deskId: string, userId?: string) =>
    officeApi.post<DeskRow>(`/desks/${deskId}/assign/`, userId ? { user_id: userId } : {}),
  releaseDesk: (deskId: string) => officeApi.post<DeskRow>(`/desks/${deskId}/release/`, {}),

  // ------------------------------------------------------------- directory
  listUsers: (opts: { team?: string; search?: string } = {}) =>
    officeApi.get<Paginated<UserSummary>>(`/users/${query(opts)}`),
  getUser: (id: string) => officeApi.get<UserDetail & { presence: PresenceUser | null }>(`/users/${id}/`),
  listTeams: () => officeApi.get<{ teams: string[] }>("/users/teams/"),
  myReports: () => officeApi.get<UserSummary[]>("/users/my-reports/"),

  // ----------------------------------------------------------------- leave
  listLeavePolicies: () => officeApi.get<Paginated<LeavePolicy>>("/leave/policies/"),
  updateLeavePolicy: (id: string, body: Partial<LeavePolicy>) =>
    officeApi.patch<LeavePolicy>(`/leave/policies/${id}/`, body),
  listLeaveRequests: (opts: {
    scope?: "self" | "team" | "org";
    status?: string;
    userId?: string;
    from?: string;
    to?: string;
  } = {}) =>
    officeApi.get<Paginated<LeaveRequest>>(
      `/leave/requests/${query({
        scope: opts.scope,
        status: opts.status,
        user_id: opts.userId,
        from: opts.from,
        to: opts.to,
      })}`,
    ),
  submitLeave: (body: {
    leave_type: LeaveType;
    start_date: string;
    end_date: string;
    day_part?: string;
    note?: string;
  }) => officeApi.post<LeaveRequest>("/leave/requests/", body),
  decideLeave: (id: string, decision: "approved" | "rejected", note?: string) =>
    officeApi.post<LeaveRequest>(`/leave/requests/${id}/decision/`, {
      decision,
      decision_note: note ?? "",
    }),
  cancelLeave: (id: string) => officeApi.post<LeaveRequest>(`/leave/requests/${id}/cancel/`, {}),
  getBalances: (userId?: string, year?: number) =>
    officeApi.get<LeaveBalance[]>(
      userId ? `/leave/balances/${userId}/${query({ year })}` : `/leave/balances/${query({ year })}`,
    ),
  adjustBalance: (userId: string, body: { leave_type: LeaveType; delta_days: string; reason: string; year?: number }) =>
    officeApi.post(`/leave/balances/${userId}/adjust/`, body),
  getLeaveCalendar: (opts: { from?: string; to?: string; scope?: "team" | "org"; team?: string } = {}) =>
    officeApi.get<{ from: string; to: string; entries: LeaveCalendarEntry[] }>(
      `/leave/calendar/${query(opts)}`,
    ),

  // --------------------------------------------------------------- reports
  getDailyReports: (opts: { from?: string; to?: string; userId?: string } = {}) =>
    officeApi.get<{ from: string; to: string; rows: DailyReport[] }>(
      `/reports/daily/${query({ from: opts.from, to: opts.to, user_id: opts.userId })}`,
    ),
  getToday: (userId?: string) =>
    officeApi.get<Record<string, unknown>>(`/reports/daily/today/${query({ user_id: userId })}`),
  saveDayNote: (date: string, note: string) =>
    officeApi.patch<DailyReport>(`/reports/daily/${date}/note/`, { note }),
  getRollup: (period: "weekly" | "monthly", count?: number, userId?: string) =>
    officeApi.get<{ periods: RollupPeriod[] }>(
      `/reports/rollup/${period}/${query({
        weeks: period === "weekly" ? count : undefined,
        months: period === "monthly" ? count : undefined,
        user_id: userId,
      })}`,
    ),
  getLiveBoard: () => officeApi.get<LiveBoard>("/reports/live/"),
  getTeamBoard: () => officeApi.get<LiveBoard>("/reports/team/"),
  getOrgMetrics: () => officeApi.get<OrgMetrics>("/reports/metrics/"),
  exportUrl: (kind: "attendance" | "leave", from: string, to: string) =>
    `/api/office/reports/export/${kind}/${query({ from, to })}`,

  // ---------------------------------------------------------------- social
  listKnocks: () => officeApi.get<Paginated<Knock>>("/knocks/"),
  sendKnock: (toUserId: string, intent: "chat" | "call", reason?: string) =>
    officeApi.post<Knock>("/knocks/", { to_user_id: toUserId, intent, reason: reason ?? "" }),
  answerKnock: (id: string, accepted: boolean) =>
    officeApi.post<Knock>(`/knocks/${id}/answer/`, { accepted }),
  requestRoomEntry: (zoneId: string) =>
    officeApi.post<{ request_id: string; expires_at: string }>("/room-entry/", { zone_id: zoneId }),
  answerRoomEntry: (id: string, accepted: boolean) =>
    officeApi.post(`/room-entry/${id}/answer/`, { accepted }),

  listAnnouncements: (limit = 30) =>
    officeApi.get<Paginated<Announcement>>(`/announcements/${query({ limit })}`),
  sendAnnouncement: (body: {
    scope: "org" | "team";
    title: string;
    body: string;
    team?: string;
    requires_ack?: boolean;
  }) => officeApi.post<Announcement>("/announcements/", body),
  ackAnnouncement: (id: string) => officeApi.post(`/announcements/${id}/ack/`, {}),

  listNotifications: () => officeApi.get<Paginated<OfficeNotification>>("/notifications/"),
  markNotificationsRead: (ids?: string[]) =>
    officeApi.post<{ updated: number }>("/notifications/read/", ids ? { ids } : {}),

  // ----------------------------------------------------------------- tasks
  listTasks: (opts: { repo?: string; state?: string; q?: string } = {}) =>
    officeApi.get<{
      items: OfficeTask[];
      configured: boolean;
      needs_reconnect: boolean;
      last_synced_at: string | null;
    }>(`/tasks/${query(opts)}`),
  syncTasks: () => officeApi.post<{ synced: number }>("/tasks/sync/", {}),
  focusTask: (id: string) => officeApi.post<OfficeTask>(`/tasks/${id}/focus/`, {}),
  clearFocus: () => officeApi.delete("/tasks/focus/"),
  setManualFocus: (title: string) => officeApi.patch<OfficeTask>("/tasks/focus/", { title }),
  getGitHubStatus: () => officeApi.get<Record<string, unknown>>("/tasks/github/"),

  // ----------------------------------------------------------------- admin
  getSettings: () => officeApi.get<OrgSettings>("/admin/settings/"),
  updateSettings: (body: Partial<OrgSettings>) =>
    officeApi.patch<OrgSettings>("/admin/settings/", body),
  getRoleAssignments: () =>
    officeApi.get<{
      roles: [string, string][];
      assignments: { user: UserSummary; roles: string[] }[];
    }>("/admin/roles/"),
  setRole: (userId: string, role: string, grant: boolean) =>
    officeApi.post<{ user_id: string; roles: string[] }>("/admin/roles/", {
      user_id: userId,
      role,
      grant,
    }),
  setManager: (userId: string, managerId: string | null) =>
    officeApi.post<UserSummary>("/admin/manager/", { user_id: userId, manager_id: managerId }),

  // ------------------------------------------------------------- analytics
  analytics: {
    dashboard: (q: AnalyticsQuery = {}) =>
      officeApi.get<AnalyticsDashboard>(`/reports/analytics/dashboard/${query(q)}`),
    attendance: (q: AnalyticsQuery = {}) =>
      officeApi.get<AnalyticsEnvelope & { series: AttendanceRow[]; totals: Record<string, number> }>(
        `/reports/analytics/attendance/${query(q)}`,
      ),
    statusComposition: (q: AnalyticsQuery = {}) =>
      officeApi.get<AnalyticsEnvelope & { series: CompositionRow[]; totals: Record<string, number> }>(
        `/reports/analytics/status-composition/${query(q)}`,
      ),
    utilization: (q: AnalyticsQuery = {}) =>
      officeApi.get<
        AnalyticsEnvelope & { series: UtilizationRow[]; expected_seconds_per_day: number }
      >(`/reports/analytics/utilization/${query(q)}`),
    rollup: (q: AnalyticsQuery = {}) =>
      officeApi.get<AnalyticsEnvelope & { by: string; groups: RollupGroup[] }>(
        `/reports/analytics/rollup/${query(q)}`,
      ),
    tasks: (q: AnalyticsQuery = {}) =>
      officeApi.get<AnalyticsEnvelope & TasksLoad>(`/reports/analytics/tasks/${query(q)}`),
    leave: (q: AnalyticsQuery = {}) =>
      officeApi.get<AnalyticsEnvelope & LeaveCoverage>(`/reports/analytics/leave/${query(q)}`),
    occupancy: (q: AnalyticsQuery = {}) =>
      officeApi.get<OccupancyData>(`/reports/analytics/occupancy/${query(q)}`),
    insights: (q: AnalyticsQuery = {}) =>
      officeApi.get<InsightsEnvelope>(`/reports/analytics/insights/${query(q)}`),
  },
};

export interface DeskRow {
  id: string;
  label: string;
  x: number;
  y: number;
  zone: string;
  assigned_user: UserSummary | null;
}
